// src/components/System/Role.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import {
  getRoles,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  syncRolePermissions,
  getSystemRoles,
  getOrganizationRoles,
  clearApiMessage,
  clearError,
  clearRoleState,
  selectRoles,
  selectRolePagination,
  selectCurrentRole,
  selectCurrentRolePermissions,
  selectSystemRoles,
  selectOrganizationRoles,
  selectRolesLoading,
  selectRolesApiMessage,
  selectRolesError,
} from '../../store/slices/roleSlice';
import {
  selectPermissions,
  selectPermissionCategories,
  selectCategoryDisplayNames,
  getPermissions,
  getPermissionCategories,
} from '../../store/slices/permissionSlice';
import { shouldShowApiToast } from '../../utils/helpers';
import { Key, Pencil, Trash2, AlertTriangle, Search, Hourglass, Lock, FileEdit, Loader2 } from 'lucide-react';

const Role = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [editingRoleId, setEditingRoleId] = useState(null);
  
  // ==================== REFS ====================
  const nameRef = useRef(null);
  const descriptionRef = useRef(null);
  const iconRef = useRef(null);
  const colorRef = useRef(null);
  const levelRef = useRef(null);
  const isSystemRef = useRef(null);
  const isOrgDefaultRef = useRef(null);
  const editingRoleIdRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const roles = useSelector(selectRoles) || [];
  const pagination = useSelector(selectRolePagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const currentRole = useSelector(selectCurrentRole);
  const currentRolePermissions = useSelector(selectCurrentRolePermissions) || [];
  const systemRoles = useSelector(selectSystemRoles) || [];
  const organizationRoles = useSelector(selectOrganizationRoles) || [];
  const loading = useSelector(selectRolesLoading);
  const apiMessage = useSelector(selectRolesApiMessage);
  const error = useSelector(selectRolesError);
  
  // Permission selectors
  const permissions = useSelector(selectPermissions) || [];
  const categories = useSelector(selectPermissionCategories) || [];
  const categoryDisplayNames = useSelector(selectCategoryDisplayNames) || {};

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          dispatch(getRoles({ per_page: itemsPerPage, page: currentPage })),
          dispatch(getPermissions({ per_page: 100 })),
          dispatch(getPermissionCategories()),
          dispatch(getSystemRoles()),
          dispatch(getOrganizationRoles())
        ]);
      } catch (err) {
        console.error('Error loading role data:', err);
      }
    };
    
    loadData();
    
    return () => {
      dispatch(clearRoleState());
    };
  }, [dispatch]);

  // Handle search
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        search: searchTerm,
        per_page: itemsPerPage,
        page: currentPage
      };
      dispatch(getRoles(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, currentPage, itemsPerPage, dispatch]);

  // Display API messages - with deduplication (matching Organization pattern)
  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error)) return;
    
    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== HELPERS ====================

  const getCategoryDisplayName = useCallback((categoryKey) => {
    if (!categoryKey) return 'Unknown';
    return categoryDisplayNames[categoryKey] || categoryKey;
  }, [categoryDisplayNames]);

  const getPermissionCount = (role) => {
    if (!role.permissions) return 0;
    return Array.isArray(role.permissions) ? role.permissions.length : 0;
  };

  // ==================== PAGINATION ====================
  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(pagination.last_page || 1, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== FORM HELPERS ====================
  const getFormValues = () => {
    // Get selected permissions as IDs (not slugs)
    const selectedPermIds = [];
    document.querySelectorAll('input[name="permission"]:checked').forEach(el => {
      selectedPermIds.push(parseInt(el.value)); // value is the ID
    });

    return {
      name: nameRef.current?.value?.trim() || '',
      description: descriptionRef.current?.value?.trim() || '',
      icon: iconRef.current?.value?.trim() || '👤',
      color: colorRef.current?.value || '#4f46e5',
      level: parseInt(levelRef.current?.value) || 2,
      is_system: isSystemRef.current?.checked || false,
      is_org_default: isOrgDefaultRef.current?.checked || false,
      permissions: selectedPermIds // Send IDs, not slugs
    };
  };

  const validateRoleForm = (values) => {
    const errors = {};
    
    if (!values.name) {
      errors.name = 'Role name is required';
    } else if (values.name.length < 2) {
      errors.name = 'Role name must be at least 2 characters';
    }

    if (!values.description) {
      errors.description = 'Description is required';
    } else if (values.description.length < 5) {
      errors.description = 'Description must be at least 5 characters';
    }

    if (!values.icon) {
      errors.icon = 'Please select an icon';
    }

    if (!values.color) {
      errors.color = 'Please select a color';
    }

    if (values.permissions.length === 0) {
      errors.permissions = 'Please select at least one permission';
    }

    return errors;
  };

  // ==================== CRUD OPERATIONS ====================

  const handleCreateRole = () => {
    setEditingRoleId(null);
    editingRoleIdRef.current = null;
    setSelectedPermissions([]);
    
    setTimeout(() => {
      if (nameRef.current) nameRef.current.value = '';
      if (descriptionRef.current) descriptionRef.current.value = '';
      if (iconRef.current) iconRef.current.value = '👤';
      if (colorRef.current) colorRef.current.value = '#4f46e5';
      if (levelRef.current) levelRef.current.value = '2';
      if (isSystemRef.current) isSystemRef.current.checked = false;
      if (isOrgDefaultRef.current) isOrgDefaultRef.current.checked = false;
      document.querySelectorAll('input[name="permission"]').forEach(el => el.checked = false);
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Key size={16} style={{ verticalAlign: '-3px' }} /> Create New Role</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Role Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input"
              placeholder="e.g. Worship Leader"
              ref={nameRef}
              defaultValue=""
            />
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>
              Slug will be auto-generated from the name
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input"
              placeholder="Brief description of the role"
              ref={descriptionRef}
              defaultValue=""
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Icon <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input"
                placeholder="👤"
                maxLength="2"
                ref={iconRef}
                defaultValue="👤"
              />
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>
                Emoji or text (max 2 characters)
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Color <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input"
                type="color"
                ref={colorRef}
                defaultValue="#4f46e5"
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Level</label>
            <select 
              className="form-select"
              ref={levelRef}
              defaultValue="2"
            >
              <option value={1}>Level 1 (System)</option>
              <option value={2}>Level 2 (Organization)</option>
              <option value={3}>Level 3 (Staff)</option>
              <option value={4}>Level 4 (Member)</option>
            </select>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input type="checkbox" ref={isSystemRef} />
                <span>System Role</span>
              </label>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input type="checkbox" ref={isOrgDefaultRef} />
                <span>Organization Default</span>
              </label>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Permissions <span style={{ color: 'var(--red)' }}>*</span></label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '8px',
              padding: '12px',
              background: 'var(--cream)',
              borderRadius: '8px',
              maxHeight: '250px',
              overflowY: 'auto'
            }}>
              {permissions && permissions.length > 0 ? (
                permissions.map(perm => {
                  const isDisabled = perm.is_system || false;
                  return (
                    <label key={perm.id} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px', 
                      fontSize: '12px', 
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity: isDisabled ? 0.5 : 1
                    }}>
                      <input 
                        type="checkbox" 
                        name="permission"
                        value={perm.id}
                        disabled={isDisabled}
                        defaultChecked={false}
                      /> 
                      {perm.label || perm.slug}
                      {perm.is_system && ' (System)'}
                    </label>
                  );
                })
              ) : (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: 'var(--text-light)' }}>
                  <div>No permissions available</div>
                  <div style={{ fontSize: '11px', marginTop: '4px' }}>
                    Please create permissions first in the Permissions section
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const values = getFormValues();
              const errors = validateRoleForm(values);
              
              if (Object.keys(errors).length > 0) {
                showToastMessage(Object.values(errors)[0], 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(createRole(values));
                if (result.payload?.success) {
                  closeModal();
                  await Promise.all([
                    dispatch(getRoles({ per_page: itemsPerPage, page: currentPage })),
                    dispatch(getPermissions({ per_page: 100 })),
                    dispatch(getPermissionCategories()),
                    dispatch(getSystemRoles()),
                    dispatch(getOrganizationRoles())
                  ]);
                  showToastMessage('Role created successfully!', 'success');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to create role';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Creating...</>
            ) : (
              'Create Role'
            )}
          </button>
        </div>
      </div>
    );
  };

  const handleEditRole = (role) => {
    // Check if user can edit this role (only Super Admin can edit system roles)
    if (role.is_system) {
      showToastMessage('System roles cannot be edited.', 'error');
      return;
    }

    setEditingRoleId(role.id);
    editingRoleIdRef.current = role.id;
    setSelectedPermissions(role.permissions || []);

    setTimeout(() => {
      if (nameRef.current) nameRef.current.value = role.name || '';
      if (descriptionRef.current) descriptionRef.current.value = role.description || '';
      if (iconRef.current) iconRef.current.value = role.icon || '👤';
      if (colorRef.current) colorRef.current.value = role.color || '#4f46e5';
      if (levelRef.current) levelRef.current.value = role.level || 2;
      if (isSystemRef.current) isSystemRef.current.checked = role.is_system || false;
      if (isOrgDefaultRef.current) isOrgDefaultRef.current.checked = role.is_org_default || false;
      
      // Check permissions by ID
      document.querySelectorAll('input[name="permission"]').forEach(el => {
        const permId = parseInt(el.value);
        el.checked = role.permissions?.includes(permId) || false;
      });
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Role: {role.name}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Role Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input"
              ref={nameRef}
              defaultValue={role.name || ''}
            />
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>
              Slug will be auto-generated if name changes
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input"
              ref={descriptionRef}
              defaultValue={role.description || ''}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Icon <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input"
                maxLength="2"
                ref={iconRef}
                defaultValue={role.icon || '👤'}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Color <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input"
                type="color"
                ref={colorRef}
                defaultValue={role.color || '#4f46e5'}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Level</label>
            <select 
              className="form-select"
              ref={levelRef}
              defaultValue={role.level || 2}
            >
              <option value={1}>Level 1 (System)</option>
              <option value={2}>Level 2 (Organization)</option>
              <option value={3}>Level 3 (Staff)</option>
              <option value={4}>Level 4 (Member)</option>
            </select>
          </div>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input type="checkbox" ref={isSystemRef} defaultChecked={role.is_system || false} />
                <span>System Role</span>
              </label>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input type="checkbox" ref={isOrgDefaultRef} defaultChecked={role.is_org_default || false} />
                <span>Organization Default</span>
              </label>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Permissions <span style={{ color: 'var(--red)' }}>*</span></label>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '8px',
              padding: '12px',
              background: 'var(--cream)',
              borderRadius: '8px',
              maxHeight: '250px',
              overflowY: 'auto'
            }}>
              {permissions && permissions.length > 0 ? (
                permissions.map(perm => {
                  const isDisabled = perm.is_system || false;
                  return (
                    <label key={perm.id} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px', 
                      fontSize: '12px', 
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity: isDisabled ? 0.5 : 1
                    }}>
                      <input 
                        type="checkbox" 
                        name="permission"
                        value={perm.id}
                        disabled={isDisabled}
                        defaultChecked={role.permissions?.includes(perm.id) || false}
                      /> 
                      {perm.label || perm.slug}
                      {perm.is_system && ' (System)'}
                    </label>
                  );
                })
              ) : (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: 'var(--text-light)' }}>
                  <div>No permissions available</div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const values = getFormValues();
              const errors = validateRoleForm(values);
              
              if (Object.keys(errors).length > 0) {
                showToastMessage(Object.values(errors)[0], 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(updateRole({ 
                  id: role.id, 
                  data: values 
                }));
                if (result.payload?.success) {
                  closeModal();
                  await Promise.all([
                    dispatch(getRoles({ per_page: itemsPerPage, page: currentPage })),
                    dispatch(getPermissions({ per_page: 100 })),
                    dispatch(getPermissionCategories()),
                    dispatch(getSystemRoles()),
                    dispatch(getOrganizationRoles())
                  ]);
                  showToastMessage('Role updated successfully!', 'success');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to update role';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Saving...</>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteRole = (role) => {
    if (role.is_system) {
      showToastMessage('System roles cannot be deleted.', 'error');
      return;
    }

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Role</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete the role <strong>{role.name}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> Users with this role will lose their permissions. This action cannot be undone.
          </div>
          {role.users_count > 0 && (
            <div style={{ 
              marginTop: '12px',
              padding: '12px',
              background: 'var(--amber-bg)',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'var(--amber)'
            }}>
              <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> {role.users_count} user(s) currently have this role. They will lose access.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deleteRole(role.id));
                if (result.payload?.success) {
                  closeModal();
                  await Promise.all([
                    dispatch(getRoles({ per_page: itemsPerPage, page: currentPage })),
                    dispatch(getPermissions({ per_page: 100 })),
                    dispatch(getPermissionCategories()),
                    dispatch(getSystemRoles()),
                    dispatch(getOrganizationRoles())
                  ]);
                  showToastMessage(`Role "${role.name}" deleted successfully.`, 'success');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to delete role';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Deleting...</>
            ) : (
              'Delete Role'
            )}
          </button>
        </div>
      </div>
    );
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderPagination = () => {
    if (pagination.last_page <= 1) return null;

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px',
        marginTop: '20px',
        padding: '16px',
        background: 'var(--white)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow)'
      }}>
        <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
          Showing <strong>{((currentPage - 1) * itemsPerPage) + 1}</strong> to{' '}
          <strong>{Math.min(currentPage * itemsPerPage, pagination.total)}</strong> of{' '}
          <strong>{pagination.total}</strong> roles
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
            <select
              className="form-select"
              style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              disabled={loading}
            >
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={36}>36</option>
              <option value={48}>48</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1 || loading}
              style={{
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              ← Prev
            </button>

            {getPageNumbers().map(page => (
              <button
                key={page}
                className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => goToPage(page)}
                disabled={loading}
              >
                {page}
              </button>
            ))}

            <button
              className="btn btn-ghost btn-sm"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === pagination.last_page || loading}
              style={{
                opacity: currentPage === pagination.last_page ? 0.5 : 1,
                cursor: currentPage === pagination.last_page ? 'not-allowed' : 'pointer'
              }}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ==================== MAIN RENDER ====================

  return (
    <div className="role-management">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: 'var(--navy)' }}>
            <Key size={20} style={{ verticalAlign: '-3px' }} /> Roles & Permissions
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
            Create and manage roles, assign permissions to control user access.
          </p>
        </div>
        <button className="btn btn-gold btn-sm" onClick={handleCreateRole} disabled={loading || isSubmitting}>
          + Create Role
        </button>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
            <span className="search-icon"><Search size={16} /></span>
            <input 
              type="text" 
              placeholder="Search roles..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading}
            />
          </div>
          <span style={{ fontSize: '13px', color: 'var(--text-light)' }}>
            {roles.length} roles found
          </span>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
          <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading roles...</div>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            {roles.map(role => {
              // Map permission IDs to permission objects for display
              const rolePermissions = (role.permissions || []).map(permId => 
                permissions.find(p => p.id === permId)
              ).filter(Boolean);
              
              return (
                <div key={role.id} className="card" style={{ borderTop: `3px solid ${role.color || '#4f46e5'}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ 
                      width: '44px', 
                      height: '44px', 
                      borderRadius: '50%', 
                      background: `${role.color || '#4f46e5'}18`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px'
                    }}>
                      {role.icon || '👤'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--navy)' }}>
                        {role.name}
                        {role.is_system && (
                          <span style={{ fontSize: '10px', fontWeight: '400', color: 'var(--text-light)', marginLeft: '6px' }}>
                            (System)
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                        {role.description}
                      </div>
                    </div>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '10px', 
                    background: 'var(--cream)', 
                    borderRadius: '8px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>
                        {getPermissionCount(role)}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Permissions</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>
                        Level {role.level || 2}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Level</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>
                        {role.is_system ? <Lock size={18} /> : <FileEdit size={18} />}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Type</div>
                    </div>
                  </div>

                  {rolePermissions.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {rolePermissions.slice(0, 4).map(perm => (
                          <span key={perm.id} className="badge badge-blue" style={{ fontSize: '9px' }}>
                            {perm.label || perm.slug}
                          </span>
                        ))}
                        {rolePermissions.length > 4 && (
                          <span className="badge badge-navy" style={{ fontSize: '9px' }}>
                            +{rolePermissions.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button 
                      className="btn btn-ghost btn-sm" 
                      style={{ flex: 1 }}
                      onClick={() => handleEditRole(role)}
                      title="Edit role"
                      disabled={loading || isSubmitting}
                    >
                      <Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit
                    </button>
                    {!role.is_system && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteRole(role)}
                        title="Delete role permanently"
                        disabled={loading || isSubmitting}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {renderPagination()}
        </>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .form-input.error,
        .form-select.error {
          border-color: var(--red);
          background-color: var(--red-bg);
        }
        
        .form-input.error:focus,
        .form-select.error:focus {
          border-color: var(--red);
          box-shadow: 0 0 0 3px rgba(192, 57, 43, 0.15);
        }
        
        .form-error {
          color: var(--red);
          font-size: 11px;
          margin-top: 4px;
        }
        
        .badge-blue {
          background: var(--blue);
          color: white;
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
          padding: 2px 10px;
          border-radius: 12px;
          font-size: 10px;
        }
        
        @media (max-width: 1024px) {
          .role-management .roles-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        
        @media (max-width: 600px) {
          .role-management .roles-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Role;